import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import Index from "./pages/Index";

// Lazy-loaded pages — only downloaded when navigated to
const NotFound = lazy(() => import("./pages/NotFound"));
const AboutNSI = lazy(() => import("./pages/about/AboutNSI"));
const OurTeam = lazy(() => import("./pages/about/OurTeam"));
const Concept = lazy(() => import("./pages/sez/Concept"));
const WhyCentralAsia = lazy(() => import("./pages/sez/WhyCentralAsia"));
const InvestmentOpportunities = lazy(() => import("./pages/sez/InvestmentOpportunities"));
const DryPort = lazy(() => import("./pages/development/DryPort"));
const ManufacturingPark = lazy(() => import("./pages/development/ManufacturingPark"));
const FinancialCenter = lazy(() => import("./pages/development/FinancialCenter"));
const RetailShopping = lazy(() => import("./pages/development/RetailShopping"));
const Hospitality = lazy(() => import("./pages/development/Hospitality"));
const SustainableEnergy = lazy(() => import("./pages/development/SustainableEnergy"));
const Logistics = lazy(() => import("./pages/development/Logistics"));
const MedicalResearch = lazy(() => import("./pages/development/MedicalResearch"));
const ConventionCenter = lazy(() => import("./pages/development/ConventionCenter"));
const CustomsFacilities = lazy(() => import("./pages/development/CustomsFacilities"));
const Locations = lazy(() => import("./pages/Locations"));
const Gallery = lazy(() => import("./pages/Gallery"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminRoute = lazy(() => import("./components/AdminRoute"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const News = lazy(() => import("./pages/News"));
const Contact = lazy(() => import("./pages/Contact"));

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Layout>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about/nsi" element={<AboutNSI />} />
              <Route path="/about/team" element={<OurTeam />} />
              <Route path="/sez/concept" element={<Concept />} />
              <Route path="/sez/why-central-asia" element={<WhyCentralAsia />} />
              <Route path="/sez/investment" element={<InvestmentOpportunities />} />
              <Route path="/development/dry-port" element={<DryPort />} />
              <Route path="/development/manufacturing" element={<ManufacturingPark />} />
              <Route path="/development/financial-center" element={<FinancialCenter />} />
              <Route path="/development/retail" element={<RetailShopping />} />
              <Route path="/development/hospitality" element={<Hospitality />} />
              <Route path="/development/energy" element={<SustainableEnergy />} />
              <Route path="/development/logistics" element={<Logistics />} />
              <Route path="/development/medical" element={<MedicalResearch />} />
              <Route path="/development/convention" element={<ConventionCenter />} />
              <Route path="/development/customs" element={<CustomsFacilities />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/news" element={<News />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminRoute><AdminPanel /></AdminRoute></Suspense>} />
              <Route path="/gallery/admin" element={<Suspense fallback={<PageLoader />}><AdminRoute><AdminPanel /></AdminRoute></Suspense>} />
              <Route path="/news/admin" element={<Suspense fallback={<PageLoader />}><AdminRoute><AdminPanel /></AdminRoute></Suspense>} />
              <Route path="/admin/contacts" element={<Suspense fallback={<PageLoader />}><AdminRoute><AdminPanel /></AdminRoute></Suspense>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Layout>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
